import { rtkApi, GET_STUDENT_REQUESTS } from '@/shared/api';
import { createFormData } from '@/shared/lib/utils/createFormData';
import { SuccessApiResponse } from '@/shared/types/common';
import {
  ICreateStudentRequestPayload,
  IDeleteStudentRequestResponse,
  IStudentRequestAttendanceFilterCourse,
  IStudentRequestAttendanceFiltersResponse,
  IStudentRequestDetail,
  IStudentRequestListRow,
  IUpdateStudentRequestPayload,
} from './types';

const hasFiles = (files?: File[]) => Boolean(files?.length);

const prepareMultipartPayload = <
  Payload extends ICreateStudentRequestPayload | Omit<IUpdateStudentRequestPayload, 'requestId'>,
>(
  payload: Payload,
) => ({
  ...payload,
  groupLessonIds: payload.groupLessonIds?.length
    ? JSON.stringify(payload.groupLessonIds)
    : undefined,
  ...('removeAttachmentKeys' in payload
    ? {
        removeAttachmentKeys: payload.removeAttachmentKeys?.length
          ? JSON.stringify(payload.removeAttachmentKeys)
          : undefined,
      }
    : {}),
});

const studentRequestsApi = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    getStudentRequestAttendanceFilters: build.query<IStudentRequestAttendanceFilterCourse[], void>({
      query: () => '/student/requests/attendance-filters',
      transformResponse: (response: IStudentRequestAttendanceFiltersResponse) =>
        response.courses ?? [],
    }),
    getStudentRequests: build.query<IStudentRequestListRow[], void>({
      query: () => '/student/requests',
      transformResponse: (response: SuccessApiResponse<{ requests: IStudentRequestListRow[] }>) =>
        response.data.requests,
      providesTags: [GET_STUDENT_REQUESTS],
    }),
    getStudentRequestDetail: build.query<IStudentRequestDetail, number>({
      query: (requestId) => `/student/requests/${requestId}`,
      transformResponse: (response: SuccessApiResponse<IStudentRequestDetail>) => response.data,
      providesTags: (_result, _error, requestId) => [{ type: GET_STUDENT_REQUESTS, id: requestId }],
    }),
    createStudentRequest: build.mutation<IStudentRequestDetail, ICreateStudentRequestPayload>({
      query: (payload) => ({
        url: '/student/requests',
        method: 'POST',
        body: hasFiles(payload.studentAttachments)
          ? createFormData(prepareMultipartPayload(payload))
          : payload,
      }),
      transformResponse: (response: SuccessApiResponse<IStudentRequestDetail>) => response.data,
      invalidatesTags: [GET_STUDENT_REQUESTS],
    }),
    updateStudentRequest: build.mutation<IStudentRequestDetail, IUpdateStudentRequestPayload>({
      query: ({ requestId, ...payload }) => ({
        url: `/student/requests/${requestId}`,
        method: 'PUT',
        body: hasFiles(payload.studentAttachments)
          ? createFormData(prepareMultipartPayload(payload))
          : payload,
      }),
      transformResponse: (response: SuccessApiResponse<IStudentRequestDetail>) => response.data,
      invalidatesTags: (_result, _error, { requestId }) => [
        GET_STUDENT_REQUESTS,
        { type: GET_STUDENT_REQUESTS, id: requestId },
      ],
    }),
    deleteStudentRequest: build.mutation<IDeleteStudentRequestResponse, number>({
      query: (requestId) => ({
        url: `/student/requests/${requestId}`,
        method: 'DELETE',
      }),
      transformResponse: (response: SuccessApiResponse<IDeleteStudentRequestResponse>) =>
        response.data,
      invalidatesTags: [GET_STUDENT_REQUESTS],
    }),
  }),
});

export const {
  useGetStudentRequestAttendanceFiltersQuery,
  useGetStudentRequestsQuery,
  useGetStudentRequestDetailQuery,
  useCreateStudentRequestMutation,
  useUpdateStudentRequestMutation,
  useDeleteStudentRequestMutation,
} = studentRequestsApi;
